import { Button, Box } from '@mui/material';
import { useRef, useState } from 'react';

import { ExcelIcon } from 'components/SampleEditor/icons/ExcelIcon';

import { useLazyGetSamplesPaginatedQuery } from 'store/apis/samplesApi';
import { EXPORT_SAMPLES_FILE_ERROR_MESSAGE } from 'constants/errorMessages';
import { Spinner } from 'components/Loading/Spinner/Spinner';
import { useSnackbar } from 'utilities/hooks/useSnackbar/useSnackbar';
import { getAllGridData } from 'components/OrderForm/SamplesFormV2/utilities';
import { buildSamplesState } from 'components/SampleEditor/helpers/functions';
import { useExcelStyles } from 'components/SampleEditor/components/styles';
import { useLocation } from 'react-router-dom';
import { SAMPLE_EDITOR_URL } from 'constants/urls';
import { ExportAllGrid } from 'components/SampleEditor/ExportAllGrid';
import {
  useExportGridDataToExcel,
  useImportExcelToGridTable,
} from 'components/SampleEditor/hooks';

export const ExcelActionButtons = ({
  setSelectedRows = () => {},
  isDisabledExport = false,
  gridAPI = false,
  fileName = '',
  sheetName = 'Samples',
  importGridAPIHandler = () => {},
  disabled = false,
  fullColumns = [],
}) => {
  const { classes } = useExcelStyles();
  const fileInputRef = useRef(null);

  const { pathname } = useLocation();

  const isSampleEditorPage = pathname.includes(SAMPLE_EDITOR_URL);

  const [exportGridApi, setExportGridApi] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const [getExportSamples] = useLazyGetSamplesPaginatedQuery();

  const { showError } = useSnackbar();

  const { handleSetFile, isUploading } = useImportExcelToGridTable({
    isSampleEditorPage,
    importGridAPIHandler,
  });
  const exportGridDataToExcel = useExportGridDataToExcel({
    sheetName,
    fileName,
  });

  const exportSamplesCount = gridAPI?.getSelectedRows().length;

  const importHandler = () => {
    fileInputRef.current.click();
  };

  const deselectHandler = () => {
    const selectedRows = gridAPI.getSelectedRows();
    setSelectedRows([]);
    gridAPI.deselectAll();
    gridAPI.redrawRows(selectedRows);
  };

  const exportHandler = () => {
    if (!gridAPI) return;

    const selectedRows = gridAPI.getSelectedRows();
    const allTableData = getAllGridData(gridAPI);

    if (!allTableData.length) return;

    exportGridDataToExcel({ gridAPI, onlySelected: !!selectedRows.length });
    deselectHandler();
  };

  const exportAllHandler = async () => {
    setIsGenerating(true);

    const { data, error } = await getExportSamples({
      page: null,
      limit: 100000000,
    });

    if (error) {
      showError(EXPORT_SAMPLES_FILE_ERROR_MESSAGE);
      setIsGenerating(false);

      return;
    }

    if (!exportGridApi) return;

    const samples = data?.results;

    exportGridApi.gos.api.setRowData(buildSamplesState(samples ?? []));

    const allTableData = getAllGridData(exportGridApi);

    if (!allTableData.length) return;

    exportGridDataToExcel({ gridAPI: exportGridApi, onlySelected: false });
    setIsGenerating(false);
  };

  const exportButtonTitle = ` EXPORT ${exportSamplesCount || ''} SAMPLE${
    exportSamplesCount === 1 ? '' : 'S'
  } TO XLSX`;

  return (
    <Box className={classes.flexWrapper}>
      {isSampleEditorPage && (
        <>
          <ExportAllGrid
            setExportGridApi={setExportGridApi}
            fullColumns={fullColumns}
          />
          <Button
            startIcon={
              isGenerating ? (
                <Spinner size={20} />
              ) : (
                <ExcelIcon disabled={isGenerating} />
              )
            }
            variant="contained"
            color="secondary"
            className={classes.excelButton}
            disabled={isGenerating}
            onClick={exportAllHandler}
          >
            {isGenerating ? 'GENERATING' : 'EXPORT ALL SAMPLES TO XLSX'}
          </Button>
        </>
      )}

      <Button
        startIcon={<ExcelIcon disabled={isDisabledExport || disabled} />}
        variant="contained"
        color="secondary"
        className={classes.excelButton}
        disabled={isDisabledExport || disabled}
        onClick={exportHandler}
      >
        {exportButtonTitle}
      </Button>
      <Button
        startIcon={<ExcelIcon disabled={isUploading || disabled} />}
        variant="contained"
        color="secondary"
        className={classes.excelButton}
        onClick={importHandler}
        disabled={isUploading || disabled}
        endIcon={isUploading && <Spinner size={20} />}
      >
        IMPORT XLSX TO SAMPLES
      </Button>
      <input
        onChange={handleSetFile}
        type="file"
        ref={fileInputRef}
        className={classes.excelInput}
      />
    </Box>
  );
};
